import React, { useState, useEffect } from 'react';
import { 
  Save, 
  Plus, 
  Trash2, 
  Upload, 
  Image as ImageIcon,
  Eye,
  Edit3
} from 'lucide-react';
import { ourTeamAPI, getImageUrl } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  OurTeamContainer,
  SectionHeader,
  Title,
  Subtitle,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  TeamMembersContainer,
  MemberCard,
  MemberHeader,
  MemberTitle,
  RemoveMemberButton,
  ImageUpload,
  ImageInput,
  ImageLabel,
  ImagePreview,
  ImagePlaceholder,
  ActionButton,
  SubmitButton,
  LoadingSpinner,
  ErrorMessage,
  PreviewSection,
  PreviewTitle,
  PreviewContent,
  PreviewHeading,
  PreviewParagraph,
  TeamMembersPreview,
  MemberPreviewCard,
  MemberPhoto,
  MemberPhotoPlaceholder,
  MemberPreviewName,
  MemberPreviewRole,
  MemberPreviewExperience,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText
} from './OurTeamSection.styles';

const OurTeamSection = () => {
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState({
    first_heading: '',
    first_paragraph: '',
    second_heading: '',
    second_paragraph: '',
    team_members: []
  });
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    loadOurTeam();
  }, []);

  const loadOurTeam = async () => {
    try {
      setLoading(true);
      const response = await ourTeamAPI.get();
      if (response.status === 200) {
        const data = response.data;
        setFormData({
          first_heading: data.first_heading || '',
          first_paragraph: data.first_paragraph || '',
          second_heading: data.second_heading || '',
          second_paragraph: data.second_paragraph || '',
          team_members: data.team_members || []
        });
        setExistingData(data);
        
        // Set up image mapping for existing photos
        if (data.team_members) {
          const imageMap = {};
          data.team_members.forEach((member, index) => {
            if (member.photo_path) {
              imageMap[index] = member.photo_path;
            }
          });
          setImages(imageMap);
        }
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        const errorMessage = error.response?.data?.message || 'Failed to load our team data';
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addTeamMember = () => {
    setFormData(prev => ({
      ...prev,
      team_members: [...prev.team_members, {
        name: '',
        role: '',
        experience_summary: ''
      }]
    }));
  };

  const removeTeamMember = (index) => {
    setFormData(prev => ({
      ...prev,
      team_members: prev.team_members.filter((_, i) => i !== index)
    }));
    
    // Remove associated image
    setImages(prev => {
      const newImages = { ...prev };
      delete newImages[index];
      return newImages;
    });
  };

  const handleMemberChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      team_members: prev.team_members.map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  const handleImageChange = (index, file) => {
    if (file) {
      setImages(prev => ({
        ...prev,
        [index]: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_heading.trim()) {
      setError('First heading is required');
      return;
    }

    if (!formData.first_paragraph.trim()) {
      setError('First paragraph is required');
      return;
    }

    if (!formData.second_heading.trim()) {
      setError('Second heading is required');
      return;
    }

    if (!formData.second_paragraph.trim()) {
      setError('Second paragraph is required');
      return;
    }

    if (formData.team_members.length === 0) {
      setError('At least one team member is required');
      return;
    }

    // Validate each team member
    for (let i = 0; i < formData.team_members.length; i++) {
      const member = formData.team_members[i];
      if (!member.name.trim() || !member.role.trim() || !member.experience_summary.trim()) {
        setError(`Team member ${i + 1}: All fields are required`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = new FormData();
      submitData.append('first_heading', formData.first_heading);
      submitData.append('first_paragraph', formData.first_paragraph);
      submitData.append('second_heading', formData.second_heading);
      submitData.append('second_paragraph', formData.second_paragraph);
      submitData.append('team_members', JSON.stringify(formData.team_members));

      // Add image files
      Object.keys(images).forEach(index => {
        if (images[index] instanceof File) {
          submitData.append(`member_${index}_photo`, images[index]);
        }
      });

      const response = existingData 
        ? await ourTeamAPI.update(submitData)
        : await ourTeamAPI.create(submitData);

      if (response.status === 200 || response.status === 201) {
        showSuccess(existingData ? 'Our team section updated successfully!' : 'Our team section created successfully!');
        setExistingData(response.data);
        loadOurTeam(); // Reload to get updated data
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to save our team section';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !existingData) {
    return (
      <OurTeamContainer>
        <SectionHeader>
          <Title>Our Team Section</Title>
          <Subtitle>Loading...</Subtitle>
        </SectionHeader>
      </OurTeamContainer>
    );
  }

  return (
    <OurTeamContainer>
      <SectionHeader>
        <Title>Our Team Section Management</Title>
        <Subtitle>Manage your website's team section with headings, paragraphs, and team member profiles</Subtitle>
      </SectionHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="first_heading">First Heading *</Label>
          <Input
            id="first_heading"
            type="text"
            value={formData.first_heading}
            onChange={(e) => handleInputChange('first_heading', e.target.value)}
            placeholder="e.g., Meet Our Team"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="first_paragraph">First Paragraph *</Label>
          <TextArea
            id="first_paragraph"
            value={formData.first_paragraph}
            onChange={(e) => handleInputChange('first_paragraph', e.target.value)}
            placeholder="Brief introduction about your team..."
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="second_heading">Second Heading *</Label>
          <Input
            id="second_heading"
            type="text"
            value={formData.second_heading}
            onChange={(e) => handleInputChange('second_heading', e.target.value)}
            placeholder="e.g., Our Commitment to Excellence"
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="second_paragraph">Second Paragraph *</Label>
          <TextArea
            id="second_paragraph"
            value={formData.second_paragraph}
            onChange={(e) => handleInputChange('second_paragraph', e.target.value)}
            placeholder="Detailed explanation of your team's philosophy..."
            required
            style={{ minHeight: '120px' }}
          />
        </FormGroup>

        <TeamMembersContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <Label>Team Members *</Label>
            <ActionButton type="button" onClick={addTeamMember}>
              <Plus size={16} />
              Add Team Member
            </ActionButton>
          </div>

          {formData.team_members.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>👥</EmptyStateIcon>
              <EmptyStateText>No team members added yet. Click "Add Team Member" to get started.</EmptyStateText>
            </EmptyState>
          ) : (
            formData.team_members.map((member, index) => (
              <MemberCard key={index}>
                <MemberHeader>
                  <MemberTitle>Team Member {index + 1}</MemberTitle>
                  <RemoveMemberButton type="button" onClick={() => removeTeamMember(index)}>
                    <Trash2 size={14} />
                    Remove
                  </RemoveMemberButton>
                </MemberHeader>

                <FormGroup>
                  <Label htmlFor={`member-${index}-name`}>Name *</Label>
                  <Input
                    id={`member-${index}-name`}
                    type="text"
                    value={member.name}
                    onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                    placeholder="e.g., John Doe"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor={`member-${index}-role`}>Role *</Label>
                  <Input
                    id={`member-${index}-role`}
                    type="text"
                    value={member.role}
                    onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                    placeholder="e.g., Chief Executive Officer"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor={`member-${index}-experience`}>Experience Summary *</Label>
                  <TextArea
                    id={`member-${index}-experience`}
                    value={member.experience_summary}
                    onChange={(e) => handleMemberChange(index, 'experience_summary', e.target.value)}
                    placeholder="Brief summary of the team member's experience and background..."
                    required
                    style={{ minHeight: '100px' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Photo (Optional)</Label>
                  <ImageUpload>
                    <ImageInput
                      type="file"
                      id={`member-${index}-photo`}
                      accept="image/*"
                      onChange={(e) => handleImageChange(index, e.target.files[0])}
                    />
                    <ImageLabel htmlFor={`member-${index}-photo`}>
                      <Upload size={16} />
                      Choose Photo
                    </ImageLabel>
                    <ImagePreview>
                      {images[index] ? (
                        images[index] instanceof File ? (
                          <img
                            src={URL.createObjectURL(images[index])}
                            alt={`Preview ${member.name || 'team member'}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <img
                            src={getImageUrl(images[index])}
                            alt={member.name || 'team member'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )
                      ) : (
                        <ImagePlaceholder>
                          <ImageIcon size={24} />
                          <span>No photo</span>
                        </ImagePlaceholder>
                      )}
                    </ImagePreview>
                  </ImageUpload>
                </FormGroup>
              </MemberCard>
            ))
          )}
          
          {formData.team_members.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
              <ActionButton type="button" onClick={addTeamMember}>
                <Plus size={16} />
                Add Another Team Member
              </ActionButton>
            </div>
          )}
        </TeamMembersContainer>

        <SubmitButton type="submit" disabled={loading}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <Save size={20} />
              {existingData ? 'Update Our Team Section' : 'Create Our Team Section'}
            </>
          )}
        </SubmitButton>
      </Form>

      {existingData && (
        <PreviewSection>
          <PreviewTitle>
            <Eye size={20} />
            Preview
          </PreviewTitle>
          <PreviewContent>
            <PreviewHeading>{existingData.first_heading}</PreviewHeading>
            <PreviewParagraph>{existingData.first_paragraph}</PreviewParagraph>
            
            <PreviewHeading style={{ marginTop: '3rem' }}>{existingData.second_heading}</PreviewHeading>
            <PreviewParagraph>{existingData.second_paragraph}</PreviewParagraph>
            
            {existingData.team_members && existingData.team_members.length > 0 && (
              <TeamMembersPreview>
                {existingData.team_members.map((member, index) => (
                  <MemberPreviewCard key={index}>
                    <MemberPhoto>
                      {member.photo_path ? (
                        <img
                          src={getImageUrl(member.photo_path)}
                          alt={member.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <MemberPhotoPlaceholder>👤</MemberPhotoPlaceholder>
                      )}
                    </MemberPhoto>
                    <MemberPreviewName>{member.name}</MemberPreviewName>
                    <MemberPreviewRole>{member.role}</MemberPreviewRole>
                    <MemberPreviewExperience>{member.experience_summary}</MemberPreviewExperience>
                  </MemberPreviewCard>
                ))}
              </TeamMembersPreview>
            )}
          </PreviewContent>
        </PreviewSection>
      )}

    </OurTeamContainer>
  );
};

export default OurTeamSection;
